// Libraries
import { useRef, useEffect } from 'react';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

/* Seo */
import { LocationPageGenericSeo } from '@scenes/LocationPage/LocationPage.SEO';

/* Store */
import { useResponsiveIsMobile } from '@store/client/store.responsive';
import { useNavigationStoreActions } from '@store/client/store.navigation';

/* Utils */
import { isBrowser } from '@utils/environment';

/* Components */
import LazyComponent from '@components/LazyComponent';
import CustomerReviewsDynamic from '@components/UI/Reviews';
import LocationHeaderImage from '@scenes/LocationPage/Parts/LocationHeaderImage';
import PressBlock from '@scenes/LocationPage/Parts/PressBlock';
import LocationGeoList from '@scenes/LocationPage/Parts/LocationGeoList';

/* Utils */
import { getCityStateLocationData, getSkyLineImage } from '@scenes/LocationPage/locationPage.utils';
import { generateDynamicContentData, generateDynamicMetaData } from '@integrations/contentful/contentful.location.utils';

/* Types */
import { ContentBlockRendered, LocationPageProps, MetaData } from './locationPage.types';
import { IHash } from 'types/base.types';

/* Hooks */
import useToggle from '@hooks/useToggle';
import useToggleSiteNav from '@hooks/useToggleSiteNav';

/* Constants */
import { MEDIA_BLOCK, SERVICE_TODAY_BLOCK } from './locationPage.constants';

/* Styles */
import styles from './locationPage.module.scss';

/* Dynamic Components */
const MediaContentBlock = dynamic(() => import('./Parts/MediaContentBlock'));
const ContentBlock = dynamic(() => import('./Parts/ContentBlock'));
const ExpertNationWideServiceDynamic = dynamic(() => import('@components/Sku/ExpertNationWideService'), { ssr: false });
const CoveredBlockDynamic = dynamic(() => import('./Parts/CoveredBlock'), { ssr: false });
const TechInfoBlock = dynamic(() => import('./Parts/TechInfoBlock'));
const PopularServices = dynamic(() => import('./Parts/PopularServices'));
const VideoModalDynamic = dynamic(() => import('./Parts/VideoModal'));

const LocationPage = (props: LocationPageProps) => {
  /* hooks */
  const router = useRouter();

  /* Stores */
  const { setNavigationMenuPathing } = useNavigationStoreActions();
  const isMobile = useResponsiveIsMobile();

  /* Constants */
  const {
    locationData,
    reviews: { reviews },
    stats,
    geoCoordinates,
  } = props;

  /* IF we cannot derive any local data, lets redirect to /us */
  if (isBrowser() && (!locationData || !locationData.type)) {
    router.push('/us');
  }

  const reviewsRef = useRef<HTMLDivElement | null>(null);

  /* Contentful Specific Methods & Constants */
  const locationDisplayName = locationData.displayName;
  const { city, state } = getCityStateLocationData(locationData);

  /* Define our substitutions object */
  const mapper: IHash<string> = {
    city,
    state,
  };

  /* Get our data with substitutions and dynamic input data from Contentful */
  const contentBlocksData = generateDynamicContentData({ mapper, seedData: locationData.contentBlocks }) as ContentBlockRendered;
  const metaData = generateDynamicMetaData({ mapper, seedData: locationData.metaData! }) as MetaData;
  const { mediaImage, socialProofImage, mediaUrl } = MEDIA_BLOCK({ isMobile });
  const { sectionImage } = SERVICE_TODAY_BLOCK({ isMobile });
  const toggleMenuFunction = useToggleSiteNav();
  const { toggleState: showModal, setBooleanToggle: setVideoVisible } = useToggle(false);
  const toggleVideo = () => setVideoVisible(!showModal);

  useEffect(() => {
    setNavigationMenuPathing(locationData.url!);

    return () => setNavigationMenuPathing();
  }, [locationData.url, setNavigationMenuPathing]);

  return (
    <>
      <Head>
        {generateNextSeo({
          canonical: `${process.env.NEXT_PUBLIC_HOST_URL_CANONICAL}${router.asPath}`,
          title: metaData.title!,
          description: metaData.description!,
        })}
      </Head>
      <LocationPageGenericSeo locationData={locationData} stats={stats} geoCoordinates={geoCoordinates} />
      <div dangerouslySetInnerHTML={{ __html: `<!-- ${props.cacheDate} -->` }} />
      <LocationHeaderImage imageSource={getSkyLineImage(locationData)} location={city || state} />
      <LazyComponent minHeight={isMobile ? '700px' : '588px'} loader={false}>
        <MediaContentBlock
          onImageClick={toggleVideo}
          secondaryImage={socialProofImage}
          contentImage={mediaImage}
          titleText={contentBlocksData.contentOneTitle}
          onButtonClick={toggleMenuFunction}
          locationDescriptions={contentBlocksData.contentOneBody}
          buttonText="Let's Get Started"
        />
      </LazyComponent>
      {showModal && <VideoModalDynamic showModal={showModal} videoUrl={mediaUrl} toggleVideo={toggleVideo} />}
      <LazyComponent minHeight="100px" loader={false}>
        <PressBlock />
      </LazyComponent>
      <LazyComponent minHeight={isMobile ? '460px' : '575px'} loader={false}>
        <ExpertNationWideServiceDynamic />
      </LazyComponent>
      <ContentBlock
        showButton={false}
        switchColumns
        serviceImage={sectionImage}
        title={contentBlocksData.contentTwoTitle}
        cityDescriptions={contentBlocksData.contentTwoBody}
      />
      <LazyComponent minHeight={reviews ? '300px' : ''} loader={false}>
        {reviews && (
          <CustomerReviewsDynamic
            dynamicRef={reviewsRef}
            reviews={reviews}
            className={styles.review}
            carouselItemStyle={styles.reviewItem}
            headerStyles="text-align-center paddingRight-small"
            suppressAddressLine
            headerText={`What Your Neighbors Are Saying About Their ${locationDisplayName} Tech-Support Services`}
          />
        )}
      </LazyComponent>
      <LazyComponent minHeight="100px" loader={false}>
        <CoveredBlockDynamic location={locationDisplayName!} />
      </LazyComponent>
      <PopularServices location={locationDisplayName!} onButtonClick={toggleMenuFunction} isMobile={isMobile} />
      <LazyComponent minHeight="300px" loader={false}>
        <TechInfoBlock title="Meet some of our" locationOrCategory={locationDisplayName!} />
      </LazyComponent>
      <LocationGeoList geoList={props.geoList} locationData={locationData} />
    </>
  );
};

export default LocationPage;
